import { CustomFilter } from 'app/ComponentLibrary/Filter';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { trackPromise } from 'react-promise-tracker';
import { getPlacementStatusTreeOptions } from 'app/services/SharedServices/SharedServices';
import { defaultValues, getFilterSpecs } from './CandidatesFilterUtils';
import {
  selectMyCandidatesRecruiterOptions,
  selectMyCandidatesDisciplineOptions,
  selectMyCandidatesSpecialtyOptions,
  selectMyCandidatesStateOptions,
  selectMyCandidatesCandidateStatusOptions,
  selectMyCandidatesRebookedCount,
  selectMyCandidatesNotRebookedCount,
  selectMyCandidatesBookedOutOfRangeCount,
  selectMyCandidatesBookedInRangeCount,
} from '../MyCandidates.selector';
import { selectUser } from 'oidc/user.selectors';
import { Concatenate } from 'utils/string/string';
import { myCandidatesActions } from '../MyCandidates.redux';

export const CandidatesFilter = (props: {
  onApplyFilter: (values: Record<string, unknown>) => void;
  onAfterReset?: () => void;
}) => {
  const dispatch = useDispatch();
  const { setValue } = useFormContext();
  const recruiterOptions = useSelector(selectMyCandidatesRecruiterOptions);
  const disciplineOptions = useSelector(selectMyCandidatesDisciplineOptions);
  const specialtyOptions = useSelector(selectMyCandidatesSpecialtyOptions);
  const stateOptions = useSelector(selectMyCandidatesStateOptions);
  const candidateStatusOptions = useSelector(selectMyCandidatesCandidateStatusOptions);
  const [placementStatusTreeOptions, setPlacementStatusTreeOptions] = useState<any[]>([]);
  const rebookedCount = useSelector(selectMyCandidatesRebookedCount);
  const notRebookedCount = useSelector(selectMyCandidatesNotRebookedCount);
  const bookedOutOfRangeCount = useSelector(selectMyCandidatesBookedOutOfRangeCount);
  const bookedInRangeCount = useSelector(selectMyCandidatesBookedInRangeCount);
  const { userInfo } = useSelector(selectUser) ?? {};

  useEffect(() => {
    trackPromise(
      getPlacementStatusTreeOptions().then(res => setPlacementStatusTreeOptions(res || [])),
      'MyCandidates-placement-status-filter',
    );
  }, []);

  const filterSpecs = useMemo(
    () => ({
      disciplineOptions,
      specialtyOptions,
      stateOptions,
      candidateStatusOptions,
      placementStatusTreeOptions,
      rebookedCount,
      notRebookedCount,
      bookedOutOfRangeCount,
      bookedInRangeCount,
    }),
    [
      disciplineOptions,
      specialtyOptions,
      stateOptions,
      candidateStatusOptions,
      placementStatusTreeOptions,
      rebookedCount,
      notRebookedCount,
      bookedOutOfRangeCount,
      bookedInRangeCount,
    ],
  );
  const fields = useMemo(
    () => getFilterSpecs(recruiterOptions, filterSpecs),
    [recruiterOptions, filterSpecs],
  );

  const resetFilter = () => {
    if (userInfo?.employeeId) {
      const label = Concatenate([userInfo.firstName || '', userInfo.lastName || ''], ' ');
      const placementRecruiter = {
        id: userInfo.employeeId,
        name: label,
        value: userInfo.employeeId,
        object: { userId: userInfo.employeeId, firstName: userInfo.firstName, lastName: userInfo.lastName },
      };
      const newFilter = { ...defaultValues, placementRecruiter, discipline: [], specialty: [], candidateStatus: [] };
      Object.entries(newFilter).forEach(([key, val]) => setValue(key, val));

      dispatch(myCandidatesActions.setSelectedBarChartMonth(null));
      dispatch(myCandidatesActions.setRecruiterId(userInfo.employeeId));
      props.onApplyFilter(newFilter);
      props.onAfterReset?.();
    }
  };

  return (
    <CustomFilter
      fields={fields}
      onResetForm={() => resetFilter()}
      justifyContent="flex-start"
      version2
      analytics={{
        caller: 'Recruiter WorkDesk',
        onApply: true,
        properties: { category: 'My Candidates' },
      }}
    />
  );
};
